import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/Layout";
import api, { apiError } from "@/lib/api";
import { Upload, FileSpreadsheet, FileText, ClipboardList, Loader2, ArrowRight, CheckCircle2, AlertTriangle, Layers, Sunrise, Sunset } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const FIELD_LABELS = {
  route_number: "N° route", stop_number: "Ordre", student_name: "Élève", address: "Adresse",
  city: "Ville", postal_code: "Code postal", latitude: "Latitude", longitude: "Longitude",
  pickup_time: "Heure", school: "École", notes: "Notes", type: "Type",
};

export default function ImportRoute() {
  const nav = useNavigate();
  const [stops, setStops] = useState([]);
  const [routeNumber, setRouteNumber] = useState("");
  const [school, setSchool] = useState("");
  const [schoolAddress, setSchoolAddress] = useState("");
  const [arrivalMax, setArrivalMax] = useState("08:05");
  const [garages, setGarages] = useState([]);
  const [garageId, setGarageId] = useState("");
  const [period, setPeriod] = useState("AM");
  const [returnsToGarage, setReturnsToGarage] = useState(false);
  const [circuit, setCircuit] = useState("");
  const [schoolPoint, setSchoolPoint] = useState(null);
  const [batch, setBatch] = useState(null);          // {items:[...]}
  const [drivers, setDrivers] = useState([]);
  const [buses, setBuses] = useState([]);
  const [assign, setAssign] = useState({ driver_id: "", bus_id: "", date: new Date().toISOString().slice(0, 10) });
  const [busy, setBusy] = useState(false);

  // tabular state
  const [tabular, setTabular] = useState(null); // {headers, suggested_mapping, raw_rows, preview_rows}
  const [mapping, setMapping] = useState({});

  useEffect(() => {
    api.get("/garages").then((r) => { setGarages(r.data); if (r.data.length === 1) setGarageId(r.data[0].id); }).catch(() => {});
    api.get("/drivers").then((r) => setDrivers(r.data)).catch(() => {});
    api.get("/buses").then((r) => setBuses(r.data)).catch(() => {});
  }, []);

  const onBatchFiles = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/routes/import/batch", fd);
      setBatch(data);
      const withStops = data.items.filter((i) => i.count);
      toast.success(`${withStops.length} parcours prêts sur ${data.count} fichiers`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const toggleBatchGarage = (i) => setBatch((b) => ({
    ...b, items: b.items.map((it, idx) => idx === i ? { ...it, returns_to_garage: !it.returns_to_garage } : it),
  }));

  const createBlock = async () => {
    if (!garageId) return toast.error("Sélectionnez le garage de départ");
    const items = batch.items.filter((i) => i.count).map((i) => ({
      route_number: i.detected.route_number || "?", period: i.detected.period || "AM",
      circuit_number: i.detected.circuit || "", school: i.detected.school || "",
      school_arrival_max: i.detected.school_time || "08:05",
      school_lat: i.detected.school_point?.lat ?? null, school_lng: i.detected.school_point?.lng ?? null,
      returns_to_garage: !!i.returns_to_garage, stops: i.stops,
    }));
    if (!items.length) return toast.error("Aucun parcours avec des arrêts");
    setBusy(true);
    try {
      const body = { garage_id: garageId, items, publish: true };
      if (assign.driver_id && assign.bus_id) Object.assign(body, assign);
      const { data } = await api.post("/routes/batch-create", body);
      toast.success(`${data.created.length} route(s) créées et optimisées${data.skipped.length ? ` · ${data.skipped.length} ignorée(s)` : ""}`);
      setBatch(null);
      nav("/routes");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onFile = async (e, kind) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/routes/import/preview/file", fd);
      if (data.mode === "pdf") {
        if (data.error) toast.error(data.error);
        setStops(data.stops || []);
        if (data.route_number) setRouteNumber(String(data.route_number));
        if (data.school) setSchool(data.school);
        toast.success(`${(data.stops || []).length} arrêts extraits du PDF`);
      } else {
        setTabular(data); setMapping(data.suggested_mapping || {}); setStops([]);
        const m = data.detected || {};
        if (m.route_number) setRouteNumber(String(m.route_number));
        if (m.period) setPeriod(m.period);
        if (m.circuit) setCircuit(String(m.circuit));
        if (m.school) { setSchool(m.school); setSchoolAddress(""); }
        if (m.school_time) setArrivalMax(m.school_time);
        setSchoolPoint(m.school_point || null);
        if (!m.stop_rows) toast.error(`Aucun arrêt d'élève dans ce fichier (parcours ${m.route_name || ""} — probablement « LIBRE »)`);
        else toast.success(`Parcours ${m.route_number || ""} ${m.period || ""} · ${m.stop_rows} arrêts détectés`);
      }
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const applyMapping = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/routes/import/apply-mapping", { rows: tabular.raw_rows, mapping });
      setStops(data.stops); setTabular(null);
      const rn = data.stops.find((s) => s.route_number)?.route_number;
      if (rn) setRouteNumber(String(rn));
      toast.success(`${data.count} arrêts prêts`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const parseManual = async (text) => {
    if (!text.trim()) { setStops([]); return; }
    try {
      const { data } = await api.post("/routes/import/preview/manual", { text });
      setStops(data.stops);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const createRoute = async () => {
    if (!routeNumber) return toast.error("Numéro de route requis");
    if (!garageId) return toast.error("Sélectionnez le garage de départ (créé dans le panneau Admin)");
    const kept = stops.filter((s) => s.included !== false);
    if (kept.length === 0) return toast.error("Aucun arrêt sélectionné");
    setBusy(true);
    try {
      const { data } = await api.post("/routes", {
        route_number: routeNumber, garage_id: garageId, period, circuit_number: circuit,
        school_lat: schoolPoint?.lat ?? null, school_lng: schoolPoint?.lng ?? null,
        returns_to_garage: returnsToGarage,
        school, school_address: schoolAddress || school,
        school_arrival_max: arrivalMax, stops: kept,
      });
      toast.success("Route créée — géocodage et analyse…");
      await api.post(`/routes/${data.id}/analyze`);
      nav(`/routes/${data.id}/analyze`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); setBusy(false); }
  };

  const waypoints = stops.filter((s) => s.stop_kind === "waypoint");
  const included = stops.filter((s) => s.included !== false);
  const toggleWaypoints = (on) => setStops((p) => p.map((s) => s.stop_kind === "waypoint" ? { ...s, included: on } : s));
  const toggleOne = (i) => setStops((p) => p.map((s, idx) => idx === i ? { ...s, included: s.included === false } : s));

  const editStop = (i, k, v) => setStops((p) => p.map((s, idx) => idx === i ? { ...s, [k]: v } : s));

  return (
    <div>
      <PageHeader title="Importer une route" subtitle="CSV, Excel, PDF (IA) ou saisie manuelle" icon={Upload} />
      <div className="p-8 max-w-5xl">
        <Tabs defaultValue="batch">
          <TabsList className="mb-6">
            <TabsTrigger value="batch" data-testid="tab-batch"><Layers className="w-4 h-4 mr-1" /> Bloc complet (multi-fichiers)</TabsTrigger>
            <TabsTrigger value="tabular" data-testid="tab-tabular"><FileSpreadsheet className="w-4 h-4 mr-1" /> CSV / Excel</TabsTrigger>
            <TabsTrigger value="pdf" data-testid="tab-pdf"><FileText className="w-4 h-4 mr-1" /> PDF (IA)</TabsTrigger>
            <TabsTrigger value="manual" data-testid="tab-manual"><ClipboardList className="w-4 h-4 mr-1" /> Manuel</TabsTrigger>
          </TabsList>

          <TabsContent value="batch">
            {!batch && (
              <label className="block border-2 border-dashed border-border rounded-lg p-10 text-center cursor-pointer hover:bg-secondary transition-colors">
                <input type="file" accept=".csv,.xlsx,.xls" multiple className="hidden" onChange={onBatchFiles} data-testid="file-batch" />
                {busy ? <Loader2 className="w-8 h-8 mx-auto animate-spin text-slate-400" /> : <Layers className="w-8 h-8 mx-auto text-slate-400" />}
                <div className="mt-2 font-medium">Déposer tous les fichiers d'un circuit (AM + PM)</div>
                <div className="text-xs text-muted-foreground">Parcours, période, école et retour au garage détectés automatiquement</div>
              </label>
            )}
            {batch && (
              <div className="space-y-4">
                <div className="grid md:grid-cols-4 gap-3 items-end p-4 border border-border rounded-lg">
                  <div><Label>Garage (départ / fin)</Label>
                    <Select value={garageId} onValueChange={setGarageId}>
                      <SelectTrigger className="mt-1" data-testid="batch-garage"><SelectValue placeholder="Choisir" /></SelectTrigger>
                      <SelectContent>{garages.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label>Chauffeur (optionnel)</Label>
                    <Select value={assign.driver_id} onValueChange={(v) => setAssign({ ...assign, driver_id: v })}>
                      <SelectTrigger className="mt-1" data-testid="batch-driver"><SelectValue placeholder="Aucun" /></SelectTrigger>
                      <SelectContent>{drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.first_name} {d.last_name}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label>Autobus (optionnel)</Label>
                    <Select value={assign.bus_id} onValueChange={(v) => setAssign({ ...assign, bus_id: v })}>
                      <SelectTrigger className="mt-1" data-testid="batch-bus"><SelectValue placeholder="Aucun" /></SelectTrigger>
                      <SelectContent>{buses.map((b) => <SelectItem key={b.id} value={b.id}>{b.fleet_number}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label>Date</Label>
                    <Input type="date" className="mt-1" value={assign.date} onChange={(e) => setAssign({ ...assign, date: e.target.value })} data-testid="batch-date" /></div>
                </div>

                <div className="border border-border rounded-lg divide-y divide-border overflow-hidden">
                  {batch.items.map((it, i) => {
                    const d = it.detected || {};
                    const Icon = d.period === "PM" ? Sunset : Sunrise;
                    return (
                      <div key={i} className={`px-5 py-3 grid grid-cols-12 gap-3 items-center text-sm bg-card ${it.count ? "" : "opacity-50"}`} data-testid={`batch-row-${i}`}>
                        <div className="col-span-1 flex items-center gap-1 font-mono"><Icon className="w-4 h-4 text-slate-400" />{d.period || "?"}</div>
                        <div className="col-span-2 font-semibold">Parcours {d.route_number || "?"}</div>
                        <div className="col-span-3 truncate text-muted-foreground">{d.school || "école non détectée"}</div>
                        <div className="col-span-2 text-xs">{it.count} arrêts · {it.waypoints || 0} passages</div>
                        <div className="col-span-1 text-xs font-mono">{d.school_time || "—"}</div>
                        <div className="col-span-3 text-right">
                          {it.count ? (
                            <label className="inline-flex items-center gap-2 text-xs cursor-pointer" data-testid={`batch-garage-toggle-${i}`}>
                              <input type="checkbox" checked={!!it.returns_to_garage} onChange={() => toggleBatchGarage(i)} />
                              Retour au garage
                            </label>
                          ) : <Badge variant="outline" className="text-[10px]">{it.error || "vide"}</Badge>}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex gap-3">
                  <Button onClick={createBlock} disabled={busy} data-testid="create-block">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Créer et optimiser le bloc <ArrowRight className="w-4 h-4 ml-1" /></>}
                  </Button>
                  <Button variant="outline" onClick={() => setBatch(null)} data-testid="cancel-block">Recommencer</Button>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="tabular">
            {!tabular && (
              <label className="block border-2 border-dashed border-border rounded-lg p-10 text-center cursor-pointer hover:bg-secondary transition-colors">
                <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => onFile(e, "tabular")} data-testid="file-tabular" />
                <FileSpreadsheet className="w-8 h-8 mx-auto text-slate-400" />
                <div className="mt-2 font-medium">Déposer un fichier CSV ou XLSX</div>
                <div className="text-xs text-muted-foreground">Les colonnes seront détectées automatiquement</div>
              </label>
            )}
            {tabular && (
              <div className="space-y-4">
                <h3 className="font-display font-semibold">Associer les colonnes</h3>
                <div className="grid md:grid-cols-2 gap-3">
                  {Object.keys(FIELD_LABELS).map((field) => (
                    <div key={field} className="flex items-center gap-3">
                      <Label className="w-28 shrink-0">{FIELD_LABELS[field]}</Label>
                      <Select value={mapping[field] || "__none__"} onValueChange={(v) => setMapping((m) => ({ ...m, [field]: v === "__none__" ? undefined : v }))}>
                        <SelectTrigger data-testid={`map-${field}`}><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">—</SelectItem>
                          {tabular.headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
                <Button onClick={applyMapping} disabled={busy} data-testid="apply-mapping">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Appliquer et prévisualiser"}
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="pdf">
            <label className="block border-2 border-dashed border-border rounded-lg p-10 text-center cursor-pointer hover:bg-secondary transition-colors">
              <input type="file" accept=".pdf" className="hidden" onChange={(e) => onFile(e, "pdf")} data-testid="file-pdf" />
              {busy ? <Loader2 className="w-8 h-8 mx-auto animate-spin text-slate-400" /> : <FileText className="w-8 h-8 mx-auto text-slate-400" />}
              <div className="mt-2 font-medium">Déposer un PDF de route</div>
              <div className="text-xs text-muted-foreground">Extraction par IA (GPT-5.4). Les adresses incertaines seront marquées « à valider ».</div>
            </label>
          </TabsContent>

          <TabsContent value="manual">
            <Label>Collez la liste (ex : « 1 - 07:04 - 125 rue Exemple, Chambly »)</Label>
            <Textarea rows={8} className="mt-2 font-mono text-sm" data-testid="manual-text"
              placeholder={"1 - 07:04 - 125 rue Bourgogne, Chambly\n2 - 07:08 - 223 rang des Soixante, Richelieu"}
              onChange={(e) => parseManual(e.target.value)} />
          </TabsContent>
        </Tabs>

        {stops.length > 0 && (
          <div className="mt-8 border border-border rounded-lg overflow-hidden">
            <div className="px-5 py-3 border-b border-border bg-secondary flex items-center justify-between">
              <h3 className="font-display font-semibold">Aperçu ({included.length} ramassages retenus sur {stops.length} lignes)</h3>
              <Badge variant="secondary">Validation avant importation</Badge>
            </div>
            {waypoints.length > 0 && (
              <div className="px-5 py-3 border-b border-border bg-amber-50 flex items-center justify-between gap-3" data-testid="waypoint-banner">
                <div className="text-sm text-amber-900">
                  <span className="font-semibold">{waypoints.length} point(s) de service</span> détectés comme demi-tours / transferts / bâtisses —
                  {waypoints.every((s) => s.included === false) ? " exclus des arrêts d'élèves." : " actuellement inclus."}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => toggleWaypoints(true)} data-testid="include-waypoints">Réactiver</Button>
                  <Button size="sm" variant="outline" onClick={() => toggleWaypoints(false)} data-testid="exclude-waypoints">Exclure</Button>
                </div>
              </div>
            )}
            <div className="max-h-80 overflow-y-auto divide-y divide-border">
              {stops.map((s, i) => (
                <div key={i} className={`grid grid-cols-12 gap-2 px-5 py-2 items-center text-sm ${s.included === false ? "opacity-45" : ""}`} data-testid={`preview-row-${i}`}>
                  <div className="col-span-1 font-mono text-muted-foreground">{s.sequence_original}</div>
                  <div className="col-span-2"><Input value={s.pickup_time || ""} onChange={(e) => editStop(i, "pickup_time", e.target.value)} className="h-8" /></div>
                  <div className="col-span-4"><Input value={s.address || ""} onChange={(e) => editStop(i, "address", e.target.value)} className="h-8" /></div>
                  <div className="col-span-3"><Input value={s.city || ""} onChange={(e) => editStop(i, "city", e.target.value)} className="h-8" placeholder="Ville" /></div>
                  <div className="col-span-1">
                    {s.stop_kind === "waypoint" && <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700">{s.kind_reason || "Passage"}</Badge>}
                  </div>
                  <div className="col-span-1 text-right">
                    <button onClick={() => toggleOne(i)} data-testid={`toggle-stop-${i}`} title={s.included === false ? "Réactiver" : "Exclure"}>
                      {s.included === false ? <AlertTriangle className="w-4 h-4 text-amber-500" /> : <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-5 py-4 border-t border-border grid md:grid-cols-4 gap-3 items-end">
              <div><Label>Garage (départ)</Label>
                <Select value={garageId} onValueChange={setGarageId}>
                  <SelectTrigger className="mt-1" data-testid="route-garage"><SelectValue placeholder="Choisir un garage" /></SelectTrigger>
                  <SelectContent>{garages.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Période</Label>
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger className="mt-1" data-testid="route-period"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="AM">Matin (AM)</SelectItem><SelectItem value="PM">Soir (PM)</SelectItem></SelectContent>
                </Select></div>
              <label className="flex items-center gap-2 text-sm border border-border rounded-md px-3 h-10 cursor-pointer" data-testid="route-returns-garage">
                <input type="checkbox" checked={returnsToGarage} onChange={(e) => setReturnsToGarage(e.target.checked)} />
                Dernière route du bloc — retour au garage
              </label>
              <div className="text-xs text-muted-foreground" data-testid="route-end-info">
                {returnsToGarage
                  ? "La route se terminera au garage."
                  : schoolPoint
                    ? `Destination détectée : ${schoolPoint.name} (coordonnées du fichier)`
                    : "La route se terminera à l'école (adresse géocodée)."}
                {circuit && <div className="mt-1">Circuit {circuit}</div>}
              </div>
            </div>
            <div className="px-5 pb-4 grid md:grid-cols-5 gap-3 items-end">
              <div><Label>N° de route</Label><Input value={routeNumber} onChange={(e) => setRouteNumber(e.target.value)} className="mt-1" data-testid="route-number" placeholder="25" /></div>
              <div><Label>École</Label><Input value={school} onChange={(e) => setSchool(e.target.value)} className="mt-1" data-testid="school-name" placeholder="École secondaire…" /></div>
              <div><Label>Adresse de l'école</Label><Input value={schoolAddress} onChange={(e) => setSchoolAddress(e.target.value)} className="mt-1" data-testid="school-address" placeholder="1000 boul. Industriel, Chambly" /></div>
              <div><Label>Arrivée max</Label><Input value={arrivalMax} onChange={(e) => setArrivalMax(e.target.value)} className="mt-1" data-testid="arrival-max" placeholder="08:05" /></div>
              <Button onClick={createRoute} disabled={busy} className="h-10" data-testid="create-route">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Créer & analyser <ArrowRight className="w-4 h-4 ml-1" /></>}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
